import {useOpenPanelState, useSetOpenPanel} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import type {CollapseTreeFunction} from '@github-ui/code-view-types'
import {clearDockedChatSurface, publishDockedChatSurface} from '@github-ui/copilot-chat-helpers/docked-surface-store'
import {
  CopilotSidePanelProvider,
  useCopilotSidePanelActions,
  useIsCopilotSidePanelCompact,
  useIsCopilotSidePanelOpen,
} from '@github-ui/copilot-chat-side-panel/CopilotSidePanelContext'
import {Outlet} from '@github-ui/react-router'
import {TreeOverlayBreakpoint} from '@github-ui/repos-file-tree-view'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {useDisableUserContentScrolling} from '@github-ui/use-disable-user-content-scrolling'
import {useHideFooter} from '@github-ui/use-hide-footer'
import {SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef} from 'react'

import {CopilotCodeViewIntegration} from '../components/CopilotCodeViewIntegration'
import {
  CopilotSidePanelSidebarContent,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_WIDTH_CONSTRAINTS,
} from '../components/CopilotSidePanelSidebar'
import copilotSidePanelSidebarStyles from '../components/CopilotSidePanelSidebar.module.css'
import {ReposFileTreePaneWrapper} from '../components/layout/ReposFileTreePaneWrapper'
import {SharedPageLayout} from '../components/SharedPageLayout'
import {useTreePaneContext} from '../contexts/TreePaneContext'
import {useFileTree} from '../hooks/use-file-tree'
import {useIsOverview} from '../hooks/use-is-overview'
import styles from './CodeViewFileTreeLayout.module.css'

const REPOS_FILE_TREE_ID = 'repos-file-tree'

// Keeps the Copilot side panel from crowding the file nav and symbol viewer.
// The file nav collapses on narrow screens, and the symbol viewer and Copilot
// panel stay mutually exclusive at every width.
export function useCopilotSidePanelLayoutSync(
  collapseTree: CollapseTreeFunction,
  copilotPanelOpenRef: React.RefObject<boolean>,
) {
  const isSidePanelOpen = useIsCopilotSidePanelOpen()
  const {closePanel: closeCopilotPanel} = useCopilotSidePanelActions()
  const openPanel = useOpenPanelState()
  const setOpenPanel = useSetOpenPanel()
  const symbolPanelOpen = openPanel === 'codeNav'

  // Tell useTreePane that the Copilot panel is open so it collapses the file nav
  // below TreeOverlayBreakpoint (1350px) on resize, just like the symbol viewer.
  // The panel can open while the screen is already narrow, which does not trigger
  // a resize, so we also collapse the file nav once on that open transition.
  const wasSidePanelOpenRef = useRef(isSidePanelOpen)
  useEffect(() => {
    copilotPanelOpenRef.current = isSidePanelOpen

    const justOpened = isSidePanelOpen && !wasSidePanelOpenRef.current
    if (justOpened && window.matchMedia(`(max-width: ${TreeOverlayBreakpoint - 1}px)`).matches) {
      collapseTree({setPreference: false})
    }
    wasSidePanelOpenRef.current = isSidePanelOpen
  }, [collapseTree, copilotPanelOpenRef, isSidePanelOpen])

  // The Copilot panel and the symbol viewer are mutually exclusive. They crowd
  // the file content when open together, so only one stays open at a time.
  // Whichever the user opened most recently wins, and the other yields. When
  // both somehow start open together, the symbol viewer yields first.
  const wasSymbolPanelOpenRef = useRef(symbolPanelOpen)
  useEffect(() => {
    if (isSidePanelOpen && symbolPanelOpen) {
      const symbolJustOpened = !wasSymbolPanelOpenRef.current
      if (symbolJustOpened) {
        closeCopilotPanel()
      } else {
        setOpenPanel(undefined)
      }
    }
    wasSymbolPanelOpenRef.current = symbolPanelOpen
  }, [isSidePanelOpen, symbolPanelOpen, closeCopilotPanel, setOpenPanel])
}

export function CodeViewFileTreeLayout() {
  return (
    <CopilotSidePanelProvider>
      <CodeViewFileTreeLayoutInner />
    </CopilotSidePanelProvider>
  )
}

function CodeViewFileTreeLayoutInner() {
  const treeRef = useRef<HTMLDivElement>(null)

  const isOverview = useIsOverview()

  // Publish which docked chat surface owns chat on this page so the embedded
  // Copilot panel can decide whether to auto-reopen on hard load. A file page
  // ('code-view') owns chat. The overview ('none') does not. This replaces the
  // panel's URL guess and fixed timeout.
  useEffect(() => {
    publishDockedChatSurface(isOverview ? 'none' : 'code-view')
    return () => {
      clearDockedChatSurface()
    }
  }, [isOverview])

  // While this component is mounted, disable user automatic user content scrolling
  // based on the URL hash. This scrolling is handled by the individual components
  // that require it.
  useDisableUserContentScrolling()

  // While this component is mounted, hide the standard footer. The reason we
  // must do this in javascript is because the footer is retained across Turbo
  // navigations, so if we omit it in the controller, it may still be present
  // under some circumstances.
  useHideFooter(true)

  const treePaneState = useTreePaneContext()
  const {fileTree, foldersToFetch, fileTreeProcessingTime} = useFileTree()

  useCopilotSidePanelLayoutSync(treePaneState.collapseTree, treePaneState.copilotPanelOpenRef)

  // When a tree item is selected, collapse the tree if the screen is small
  const onTreeItemSelected = useCallback(() => {
    if (window.matchMedia(`(max-width: ${ScreenSize.large - 1}px)`).matches) {
      treePaneState.collapseTree({focus: null})
    }
  }, [treePaneState])

  const sidebar = isOverview ? undefined : (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tabIndex is required for keyboard focus management in CodeViewLayout's toggleFocus
    <div ref={treeRef} className={styles.sidebar} tabIndex={0}>
      <ReposFileTreePaneWrapper
        id={REPOS_FILE_TREE_ID}
        fileTree={fileTree}
        foldersToFetch={foldersToFetch}
        processingTime={fileTreeProcessingTime}
        collapseTree={treePaneState.collapseTree}
        showTree={treePaneState.isTreeExpanded}
        onItemSelected={onTreeItemSelected}
        treeToggleElement={treePaneState.treeToggleElement}
        treeToggleRef={treePaneState.treeToggleRef}
        searchBoxRef={treePaneState.searchBoxRef}
        onFindFilesShortcut={treePaneState.onFindFilesShortcut}
        headerContent={
          <div className="d-flex width-full tmp-mb-3 flex-items-center">
            {treePaneState.isTreeExpanded && treePaneState.treeToggleElement}
            <h2 className={styles.heading}>Files</h2>
          </div>
        }
      />
    </div>
  )

  const isSidePanelEnabled = !isOverview
  const isSidePanelOpen = useIsCopilotSidePanelOpen()
  const isSidePanelCompact = useIsCopilotSidePanelCompact()

  const endSidebar = isSidePanelEnabled ? (
    // SplitPageLayout.Sidebar must be a direct React element child of
    // SplitPageLayout so Primer's useSlots can detect it and place it in
    // the correct grid area. Wrapping it in another component breaks slot
    // detection and causes it to render inside the content area instead.
    <SplitPageLayout.Sidebar
      className={clsx(
        copilotSidePanelSidebarStyles.SidePanel,
        (!isSidePanelOpen || isSidePanelCompact) && copilotSidePanelSidebarStyles.HiddenSidePanel,
      )}
      resizable
      minWidth={SIDEBAR_MIN_WIDTH}
      responsiveVariant="fullscreen"
      sticky
      position="end"
      width={SIDEBAR_WIDTH_CONSTRAINTS}
    >
      <CopilotSidePanelSidebarContent />
    </SplitPageLayout.Sidebar>
  ) : undefined

  return (
    <>
      <SharedPageLayout narrow={treePaneState.isTreeExpanded} sidebar={sidebar} endSidebar={endSidebar}>
        <Outlet />
      </SharedPageLayout>
      {isSidePanelEnabled && <CopilotCodeViewIntegration />}
    </>
  )
}
